import { observer } from 'mobx-react-lite';
import React, { FC, ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLockBodyScroll } from 'react-use';

import SidebarModal from '@/components/core/modal/SidebarModal';
import Notification from '@/components/features/notification/Notification';
import { useAnalytics } from '@/context/Analytics';
import NotificationStoreContext from '@/context/NotificationStoreContext';
import { TNotificationStore } from '@/domain/notification/NotificationStore';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';

import cn from './style.module.sass';

interface IProps {
	open: boolean;
	handleClose: () => void;
	handleOpen: () => void;
	children: ReactElement;
}

const NotificationSidebar: FC<IProps> = observer(({ open, handleClose, handleOpen, children }) => {
	const { sendEvent } = useAnalytics();
	const { t } = useTranslation();
	const {
		notifications,
		deleteNotification,
		notificationCount,
		updateVisibleNotifications,
		readNotification,
	} = useContext<TNotificationStore>(NotificationStoreContext);

	useLockBodyScroll(open);
	const debouncedReadNotification = useDebouncedCallback(() => readNotification(), 1000);

	const handleModalOpen = () => {
		sendEvent({ eventName: 'visit_notifications_page' });
		handleOpen();
	};

	const ariaLabel = notificationCount > 0
		? t('accessibility.notificationButton.withCount', { count: notificationCount })
		: t('accessibility.notificationButton.label');

	return (
		<>
			<div
				data-testid="main-button-notifications"
				className="relative size-[calc(24px*var(--font-scale,1))]"
				onClick={handleModalOpen}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter') handleModalOpen()
				}}
				aria-label={ariaLabel}
				aria-expanded={open}
				aria-controls="notification-sidebar-modal"
				aria-haspopup="dialog"
			>
				{children}{' '}
				{notificationCount > 0 && (
					<span
						className={cn.unread}
						aria-hidden="true"
					>
						{notificationCount < 100 ? notificationCount : '99+'}
					</span>
				)}
			</div>
			<SidebarModal
				isOpen={open}
				onCloseHandler={handleClose}
				handleScroll={debouncedReadNotification}
				aria-label={t('accessibility.notificationSidebar.title')}
				aria-describedby="notification-sidebar-description"
				aria-modal="true"
			>
				<div className={cn.wrapper}>
					<h2
						className={cn.title}
						id="notification-sidebar-title"
					>
						{t('common.n')}
					</h2>
					<div
						id="notification-sidebar-description"
						className="sr-only"
					>
						{t('accessibility.notificationSidebar.description')}
					</div>
					<div
						className={cn.list}
						role="region"
						aria-label={t('accessibility.notificationSidebar.list')}
						aria-describedby="notification-count"
					>
						<div id="notification-count" className="sr-only">
							{
								t('accessibility.notificationSidebar.count',
								{ count: notifications.length })
							}
						</div>
						{notifications.length > 0 &&
							notifications.map(({ id, createdAt, notification, read, files }) => (
								<Notification
									id={id}
									addToVisibleNotification={updateVisibleNotifications}
									read={read}
									deleteNotification={deleteNotification}
									createdAt={createdAt}
									notification={notification}
									files={files}
									key={id}
									aria-label={read
										? t('accessibility.notification.read')
										: t('accessibility.notification.unread')
									}
								/>
							))}
					</div>
				</div>
			</SidebarModal>
		</>
	);
});

export default NotificationSidebar;
